import {
  Steps,
  Form,
  Button,
  Card,
  message,
  ConfigProvider,
  Progress,
} from "antd";
import { useEffect, useState } from "react";
import {
  UserOutlined,
  WomanOutlined,
  TeamOutlined,
  CarOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import FamilyFatherForm from "./steps/FatherForm";
import FamilyMotherForm from "./steps/MotherForm";
import FamilyChildrenForm from "./steps/ChildrenForm";
import FamilyCarsForm from "./steps/CarsForm";
import FamilyFarmlandsForm from "./steps/FarmLands";
import { FamilyFormValues } from "../FamilyForm";
import { IFamily } from "network/models/family.model";

const toDayjs = (d?: Date | string | null) => (d ? dayjs(d) : null);

export default function FamilyFormStepper({
  onClose,
  onSubmit,
  selected,
  rtl,
  isLoading,
}: {
  onClose: () => void;
  onSubmit: (values: FamilyFormValues) => void;
  selected?: IFamily | undefined;
  rtl: boolean;
  isLoading: boolean;
}) {
  const [current, setCurrent] = useState(0);
  const [form] = Form.useForm<FamilyFormValues>();

  const next = async () => {
    try {
      await form?.validateFields();
      setCurrent((c) => c + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      message.error("يرجى إكمال البيانات المطلوبة قبل المتابعة");
    }
  };

  const prev = () => {
    setCurrent((c) => c - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const steps = [
    {
      title: "بيانات الأب",
      icon: <UserOutlined />,
      content: <FamilyFatherForm />,
    },
    {
      title: "بيانات الأم",
      icon: <WomanOutlined />,
      content: <FamilyMotherForm />,
    },
    {
      title: "الأبناء",
      icon: <TeamOutlined />,
      content: <FamilyChildrenForm />,
    },
    { title: "المركبات", icon: <CarOutlined />, content: <FamilyCarsForm /> },
    {
      title: "الأراضي الزراعية",
      icon: <EnvironmentOutlined />,
      content: <FamilyFarmlandsForm />,
    },
  ];

  useEffect(() => {
    if (!selected) {
      form.resetFields();
      return;
    }

    const data = selected;
    const initial: FamilyFormValues = {
      father: {
        ...data.father,
        birthdate: toDayjs(data.father?.birthdate as any),
      },
      mother: {
        ...data.mother,
        birthdate: toDayjs(data.mother?.birthdate as any),
      },
      children: (data.children ?? []).map((c) => ({
        ...c,
        birthdate: toDayjs(c.birthdate as any),
      })),
      cars: (data.cars ?? []).map((c) => ({
        ...c,
        registrationDate: toDayjs(c.registrationDate as any),
      })),
      farmlands: data.farmlands ?? [],
    };
    form.setFieldsValue(initial);
  }, [selected, form]);

  return (
    <ConfigProvider
      theme={{
        token: {
          fontFamily: "Tajawal, 'Segoe UI', sans-serif",
          fontSize: 16,
          colorText: "#1f1f1f",
          colorTextLabel: "#222",
        },
      }}
      direction={rtl ? "rtl" : "ltr"}
    >
      {/* <div className="flex justify-between items-center mb-4 px-2 sm:px-4">
        <h2 className="text-base sm:text-lg md:text-xl font-bold text-gray-700 flex items-center gap-2">
          📋 إدارة بيانات العائلة
        </h2>
      </div> */}
      <Progress
        percent={(current / (steps.length - 1)) * 100}
        showInfo={false}
      />

      <div className="shadow-lg rounded-2xl border border-gray-200 bg-white p-3 sm:p-5 md:p-6 w-full max-w-[95vw] sm:max-w-4xl md:max-w-5xl lg:max-w-[100%] mx-auto relative">
        {/* ✅ Responsive & Clickable Stepper */}
        <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 pb-2">
          <div className="min-w-[500px] sm:min-w-0">
            <Steps
              current={current}
              onChange={(i) => setCurrent(i)}
              items={steps.map((s, i) => ({
                key: s.title,
                title: (
                  <span className="text-[12px] sm:text-sm md:text-base font-medium whitespace-nowrap">
                    {s.title}
                  </span>
                ),
                icon: s.icon,
              }))}
              responsive
              direction="horizontal"
              className="cursor-pointer mb-4 sm:mb-6"
            />
          </div>
        </div>

        {/* ✅ Form Content */}
        <Form
          layout="vertical"
          form={form}
          preserve
          id="familyForm"
          onFinish={onSubmit}
          className="space-y-6 h-[calc(100vh-200px)]"
        >
          <div className="transition-all duration-300  h-full overflow-y-auto mb-10">
            {steps.map((step, index) => (
              <div
                key={index}
                style={{ display: current === index ? "block" : "none" }}
              >
                {step.content}
              </div>
            ))}
          </div>

          {/* Sticky Buttons */}
          <div
            className="sticky -bottom-5  left-0 right-0 bg-white py-4 px-4 sm:px-6 flex flex-col sm:flex-row justify-between items-center gap-3 rounded-lg shadow-lg border-t border-gray-200"
            style={{
              width: "100%",
              marginTop: "auto",
              zIndex: 100,
            }}
          >
            {current > 0 && (
              <Button
                onClick={prev}
                className="font-semibold px-6 w-full sm:w-auto"
                disabled={isLoading}
              >
                السابق
              </Button>
            )}
            {current < steps.length - 1 && (
              <Button
                type="primary"
                onClick={next}
                className="font-semibold px-6 w-full sm:w-auto"
                disabled={isLoading}
              >
                التالي
              </Button>
            )}
            {current === steps.length - 1 && (
              <Button
                type="primary"
                htmlType="submit"
                className="font-semibold px-6 w-full sm:w-auto"
                loading={isLoading}
                disabled={isLoading}
              >
                حفظ البيانات
              </Button>
            )}
          </div>
        </Form>
      </div>
    </ConfigProvider>
  );
}
